import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { format, differenceInHours } from "date-fns";
import { useTheme, useMediaQuery, Box, Typography, 
    Button, TextField, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, 
    FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import { getFlagUrl, formatNumber, combineDateAndTime } from "state/helpers";
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween'; 
import FlexCenter from 'components/FlexCenter'; 
import QuantityControl from 'components/QuantityControl'; 
import DeleteConfirm from 'components/DeleteConfirm';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const InventoryDialog = ({ 
    open, 
    onClose, 
    selectedInvoiceId, 
    crewCode, 
    crewName,
    selectedFlights, 
    selectedProducts, 
    selectedInventoryProducts, 
    onInvoiceSaved 
}) => {
    useAuthCheck();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    
    const [isProcessingTimeOut, setIsProcessingTimeOut] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null); 

    const [products, setProducts] = useState([]);
    const [productList, setProductList] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [airports, setAirports] = useState([]);
    const [countries, setCountries] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState('none');
    const [supplyStandards, setSupplyStandards] = useState([]);
    const [selectedSupplyStandard, setSelectedSupplyStandard] = useState('none');
    
    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const INVENTORY_OFFSET_HOURS = Number(paramsMap.get("INVENTORY_OFFSET_HOURS")) || 120;
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";  
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
   
    useEffect(() => {
        const checkFlightDate = () => {
            const now = new Date();
            const isTimeOut = selectedFlights.some(flight => {                
                return differenceInHours(now, new Date(flight.flightDate)) > INVENTORY_OFFSET_HOURS;
            });
            setIsProcessingTimeOut(isTimeOut);
        };
        checkFlightDate();
    }, [selectedFlights, INVENTORY_OFFSET_HOURS]);

      
    const fetchCountries = useCallback(async () => {
        const url = '/countries';            
        const res = await api.get(url);            
        setCountries(res.data);
    }, []);

    const fetchAirports = useCallback(async () => {
        const url = '/airports';            
        const res = await api.get(url);            
        setAirports(res.data);
    }, []);

    const fetchSuppliers = useCallback(async () => {
        const url = '/suppliers/active';            
        const res = await api.get(url);            
        setSuppliers(res.data);
    }, []);

    const fetchSupplyStandards = useCallback(async () => {
        const url = '/supplystandards/active';            
        const res = await api.get(url);            
        setSupplyStandards(res.data);
    }, []);

    const computeInitialQuantities = useMemo(() => (products) => {
        return products.reduce((acc, product) => {
            const preorderConsume = (product.preorderStockIn || 0) + 
                                    (product.preorderPreviousReceipt || 0) - 
                                    (product.preorderNextRetrieval || 0) - 
                                    (product.preorderStockOn || 0) - 
                                    (product.preorderShrinkage || 0) -
                                    (product.preorderCombo || 0) -
                                    (product.preorderLoss || 0);
            const directConsume = (product.directStockIn || 0) + 
                                  (product.directPreviousReceipt || 0) - 
                                  (product.directNextRetrieval || 0) - 
                                  (product.directStockOn || 0) - 
                                  (product.directShrinkage || 0) -
                                  (product.directCombo || 0) -
                                  (product.directLoss || 0);

            acc[product._id] = {
                preorderStockIn: product.preorderStockIn || 0,
                preorderPreviousReceipt: product.preorderPreviousReceipt || 0,
                preorderNextRetrieval: product.preorderNextRetrieval || 0,
                preorderStockOn: product.preorderStockOn || 0,
                preorderShrinkage: product.preorderShrinkage || 0,
                preorderCombo: product.preorderCombo || 0,
                preorderLoss: product.preorderLoss || 0,
                preorderConsume: preorderConsume,
                directStockIn: product.directStockIn || 0,
                directPreviousReceipt: product.directPreviousReceipt || 0,
                directNextRetrieval: product.directNextRetrieval || 0,
                directStockOn: product.directStockOn || 0,
                directShrinkage: product.directShrinkage || 0,
                directCombo: product.directCombo || 0,
                directLoss: product.directLoss || 0,
                directConsume: directConsume,
            };
            return acc;
        }, {});
    }, []);
    
    const fetchProducts = useCallback(async () => {
        const url = '/products/active';            
        const res = await api.get(url);
        const result = res.data;

        const initialProducts = result.map(product => ({
            ...product,
            purchasePrice: 0, 
        }));
        
        
        const finalProducts = selectedInventoryProducts.length > 0 
            ? selectedInventoryProducts 
            : initialProducts;
                    
        const initialQuantities = computeInitialQuantities(finalProducts);
        
        setProducts(initialProducts);
        setProductList(finalProducts);
        setQuantities(initialQuantities);
    }, [selectedInventoryProducts, computeInitialQuantities]);

    const fetchData = useCallback(async () => {
            setLoading(true);
            try {
                await fetchCountries();
                await fetchAirports();
                await Promise.allSettled([
                    fetchSuppliers(), 
                    fetchSupplyStandards(),
                    fetchProducts(),
                ]);
            } catch (error) {
                setError(error);
                toast.error(error.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
    }, [
        fetchAirports, 
        fetchCountries, 
        fetchSuppliers, 
        fetchSupplyStandards, 
        fetchProducts,
    ]);    
    
    const sectorType = useMemo(() => {
        const domesticCountryCodes = new Set(
            countries.filter(c => c?.isDefault).map(c => c?.countryCode)
        );
    
        const domesticAirports = airports?.filter(
            airport => domesticCountryCodes.has(airport?.countryCode)
        ) || [];
          
        return selectedFlights.some(flight => {
            const isDepDomestic = domesticAirports.some(airport => airport.iataCode === flight.dep);
            const isArrDomestic = domesticAirports.some(airport => airport.iataCode === flight.arr);
            return !(isDepDomestic && isArrDomestic); // true if International
        }) ? 'International' : 'Domestic';
    }, [selectedFlights, countries, airports]);      

    useEffect(() => {
        if (!open) {
            setProductList([]);
            setQuantities({});
            setSelectedSupplier('none');
            setSelectedSupplyStandard('none');
            setError(null);
            return;
        } 

        fetchData();                
    }, [open, fetchData]);
    
    const applySupplyStandardQuantities = useCallback(async (selectedId) => {    
        try {
            if (selectedId === "none") {
                // Reset quantities when no supply standard is selected
                setQuantities((prevQuantities) => {
                    const updatedQuantities = {};
                    productList.forEach(product => {
                        const existing = prevQuantities[product._id] || {};
                        updatedQuantities[product._id] = {
                            ...existing,
                            directStockIn: 0,
                            directStockOn: 0,
                            directConsume: calculateConsume("direct", {
                                ...existing,
                                directStockIn: 0,
                                directStockOn: 0,
                            }),
                        };
                    });
                    return updatedQuantities;
                });
                return;
            }

            // Find the selected supply standard
            const selectedStandard = supplyStandards.find(s => s._id === selectedId);
            if (!selectedStandard) return;

            setQuantities((prevQuantities) => {
                const updatedQuantities = { ...prevQuantities };

                // Ensure all products are initialized
                productList.forEach(product => {
                    if (!updatedQuantities[product._id]) {
                        updatedQuantities[product._id] = {
                            preorderStockIn: 0,
                            preorderPreviousReceipt: 0,
                            preorderNextRetrieval: 0,
                            preorderStockOn: 0,
                            preorderShrinkage: 0,
                            preorderCombo: 0,
                            preorderLoss: 0,
                            preorderConsume: 0,
                            directStockIn: 0,
                            directPreviousReceipt: 0,
                            directNextRetrieval: 0,
                            directStockOn: 0,
                            directShrinkage: 0,
                            directCombo: 0,
                            directLoss: 0,
                            directConsume: 0,
                        };
                    }
                });

                // Apply supply standard quantities
                selectedStandard.products.forEach((standardProduct) => {
                    const productId = standardProduct._id;

                    if (updatedQuantities[productId]) {
                        const existing = updatedQuantities[productId];
                        const newDirectStockIn = standardProduct.quantity || 0;

                        const selectedProduct = selectedProducts.length > 0 
                            ? selectedProducts.find(sp => sp._id === productId)
                            : null;
                        
                        // Use selectedProducts data when available
                        if (selectedProduct) {                            
                            const preorderQuantity = selectedProduct.preorderQuantity || 0;
                            const newPreorderStockIn = preorderQuantity > 0 ? preorderQuantity : (existing.preorderStockIn || 0);
                            const newPreorderStockOn = Math.max(newPreorderStockIn - preorderQuantity, 0);
                            const newPreorderConsume = calculateConsume("preorder", {
                                ...existing,
                                preorderStockIn: newPreorderStockIn,
                                preorderStockOn: newPreorderStockOn,
                            });

                            const directQuantity = selectedProduct.directQuantity || 0;
                            const newDirectStockOn = Math.max(newDirectStockIn - directQuantity, 0);
                            const newDirectConsume = calculateConsume("direct", {
                                ...existing,
                                directStockIn: newDirectStockIn,
                                directStockOn: newDirectStockOn,
                            });

                            updatedQuantities[productId] = {
                                ...existing,
                                preorderStockIn: newPreorderStockIn,
                                preorderStockOn: newPreorderStockOn,
                                preorderConsume: newPreorderConsume,
                                
                                directStockIn: newDirectStockIn,
                                directStockOn: newDirectStockOn,
                                directConsume: newDirectConsume,
                            };
                        } else {
                            // Use standard calculation when selectedProducts is empty or no match
                            updatedQuantities[productId] = {
                                ...existing,
                                directStockIn: newDirectStockIn,
                                directConsume: calculateConsume("direct", {
                                    ...existing,
                                    directStockIn: newDirectStockIn,
                                }),
                            };
                        }                        
                    }
                });

                return updatedQuantities;
            });

        } catch (error) {
            toast.error(error.message || "Failed to apply supply standard!");
        }        
    }, [selectedProducts, supplyStandards, productList]);
    
    useEffect(() => {
        if (!open || selectedFlights.length === 0 || suppliers.length === 0) return;

        const minEtdFlight = selectedFlights.reduce((earliest, flight) => {
            const etd = combineDateAndTime(flight.flightDate, flight.etd);
            const dep = flight.dep;
            const sector = `${flight.dep}-${flight.arr}`;
            if (!etd) return earliest;
            if (!earliest.etd) return { etd, dep, sector };
            return etd < earliest.etd ? { etd, dep, sector } : earliest;
        }, { etd: null, dep: null, sector: null });

        const dep = minEtdFlight.dep || '';

        // Auto-select supplier
        if (selectedInventoryProducts.length === 0) {
            const filteredSuppliers = suppliers.filter(supplier => 
                Array.isArray(supplier.airports) && supplier.airports.includes(dep)
            );
            if (filteredSuppliers.length > 0) {
                setSelectedSupplier(filteredSuppliers[0]._id);
            }
        }
    }, [open, selectedFlights, suppliers]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {
        if (!open || selectedFlights.length === 0 || supplyStandards.length === 0) return;

        const selectedSectors = [...new Set(selectedFlights.map(flight => `${flight.dep}-${flight.arr}`))];
        
        const minEtdFlight = selectedFlights.reduce((earliest, flight) => {
            const etd = combineDateAndTime(flight.flightDate, flight.etd);
            const dep = flight.dep;
            const sector = `${flight.dep}-${flight.arr}`;
            if (!etd) return earliest;
            if (!earliest.etd) return { etd, dep, sector };
            return etd < earliest.etd ? { etd, dep, sector } : earliest;
        }, { etd: null, dep: null, sector: null });

        const dep = minEtdFlight.dep || '';
        const minEtdSector  = minEtdFlight.sector || '';

         // Find a supply standard that includes all selected sectors
        const matchingSupplyStandard = supplyStandards.find(supplyStandard =>
            selectedSectors.every(sector => supplyStandard.sectors.includes(sector))
            && supplyStandard.sectors.every(sector => selectedSectors.includes(sector))
        ) || supplyStandards.find(supplyStandard => 
            supplyStandard.departureAirports.includes(dep) && supplyStandard.sectors.includes(minEtdSector)
        );

        if (selectedInventoryProducts.length === 0) {
            if (matchingSupplyStandard) {
                setSelectedSupplyStandard(matchingSupplyStandard._id);
                applySupplyStandardQuantities(matchingSupplyStandard._id);
            } else if (supplyStandards.length > 0) {
                setSelectedSupplyStandard("none");
                applySupplyStandardQuantities("none");
            }
        }
        
    }, [open, selectedFlights, supplyStandards, applySupplyStandardQuantities]); // eslint-disable-line react-hooks/exhaustive-deps
    
    useEffect(() => {        
        let finalProducts;

        if (selectedSupplier === 'none') {
            finalProducts = (selectedInventoryProducts.length > 0 
                ? selectedInventoryProducts.map(selectedProduct => {
                    const fullProduct = products.find(p => p._id === selectedProduct._id) || {};
                    return {
                        ...selectedProduct,
                        suppliers: fullProduct.suppliers || [],
                    };
                })
                : products.map(product => ({
                    ...product,
                    purchasePrice: 0,
                })));
        } else {
            finalProducts = (selectedInventoryProducts.length > 0 
                ? selectedInventoryProducts.map(selectedProduct => {                    
                    const fullProduct = products.find(p => p._id === selectedProduct._id) || {};
                    const supplier = fullProduct.suppliers?.find(s => s.supplierId === selectedSupplier);
                    
                    return supplier ? {
                        ...selectedProduct,
                        suppliers: fullProduct.suppliers || [],
                        supplierId: supplier.supplierId || null,
                        supplierName: supplier.supplierName || '',
                        purchasePrice: sectorType === 'International' 
                            ? (supplier.internationalPurchasePrice || 0) 
                            : (supplier.domesticPurchasePrice || 0),
                    } : null;
                }).filter(product => product !== null)
                : products
                // .filter(product => 
                //     product.suppliers.some(supplier => supplier.supplierId === selectedSupplier))
                .map(product => {
                    const supplier = product.suppliers.find(s => s.supplierId === selectedSupplier);
                    
                    return {
                        ...product,
                        supplierId: supplier?.supplierId || null,
                        supplierName: supplier?.supplierName || '',
                        purchasePrice: sectorType === 'International' 
                            ? (supplier?.internationalPurchasePrice || 0) 
                            : (supplier?.domesticPurchasePrice || 0),
                    };
                }));
        }

        setProductList(finalProducts);
        setQuantities(computeInitialQuantities(finalProducts));
    }, [
        sectorType, 
        selectedInventoryProducts, 
        selectedSupplier, 
        products, 
        computeInitialQuantities
    ]);
    
    const totalPurchaseAmounts = useMemo(() => {
        let preorderPurchaseAmount = 0;
        let directPurchaseAmount = 0;
        productList.forEach((product) => {
            const { preorderConsume, directConsume } = quantities[product._id] || {};
            preorderPurchaseAmount += (product.purchasePrice || 0) * (preorderConsume || 0);
            directPurchaseAmount += (product.purchasePrice || 0) * (directConsume || 0);
        });
        return {
            preorderPurchaseAmount,
            directPurchaseAmount,
            totalPurchaseAmount: preorderPurchaseAmount + directPurchaseAmount,
        };
    }, [productList, quantities]);

    const handleSupplierChange = (event) => {
        setSelectedSupplier(event.target.value);
    };

    const handleSupplyStandardChange = async (event) => {
        const selectedId = event.target.value;
        setSelectedSupplyStandard(selectedId);
        applySupplyStandardQuantities(selectedId);
    };  
    
    const handlePriceChange = (productId, purchasePrice) => {
        setProductList(prev => 
            prev.map(product => 
                product._id === productId ? { ...product, purchasePrice } : product
            )
        );
    };

    const calculateConsume = (type, product) => {
        const q = product || {};
        if (type === 'preorder') {
            return (
                (q.preorderStockIn || 0) +
                (q.preorderPreviousReceipt || 0) -
                (q.preorderNextRetrieval || 0) -
                (q.preorderStockOn || 0) -
                (q.preorderShrinkage || 0) -
                (q.preorderCombo || 0) -
                (q.preorderLoss || 0)
            );
        } else if (type === 'direct') {
            return (
                (q.directStockIn || 0) +
                (q.directPreviousReceipt || 0) -
                (q.directNextRetrieval || 0) -
                (q.directStockOn || 0) -
                (q.directShrinkage || 0) -
                (q.directCombo || 0) -
                (q.directLoss || 0)
            );
        }
        return 0;
    };

    const handleQuantityChange = (productId, type, newQuantity) => {
        setQuantities(prev => {
            const updatedProduct = {
                ...prev[productId],
                [type]: Math.max(0, newQuantity),
            };
    
            const updatedQuantities = {
                ...prev,
                [productId]: {
                    ...updatedProduct,
                    preorderConsume: calculateConsume('preorder', updatedProduct),
                    directConsume: calculateConsume('direct', updatedProduct),
                },
            };
    
            return updatedQuantities;
        });
    };

    const handleSaveInvoice = async () => {    
        try {
            const method = 'put';
            const url = `/inflightsales/inventory/update/${selectedInvoiceId}`;
            const payload = {
                invoiceId: selectedInvoiceId,            
                inventoryProducts: productList.map(product => ({
                    _id: product._id || null,
                    productName: product.productName,
                    unit: product.unit,
                    category: product.category,
                    supplierId: product.supplierId || null,
                    supplierName: product.supplierName,
                    purchasePrice: product.purchasePrice || 0, 
                    preorderStockIn: quantities[product._id]?.preorderStockIn || 0,
                    preorderPreviousReceipt: quantities[product._id]?.preorderPreviousReceipt || 0,
                    preorderNextRetrieval: quantities[product._id]?.preorderNextRetrieval || 0,
                    preorderStockOn: quantities[product._id]?.preorderStockOn || 0,
                    preorderShrinkage: quantities[product._id]?.preorderShrinkage || 0,
                    preorderCombo: quantities[product._id]?.preorderCombo || 0,
                    preorderLoss: quantities[product._id]?.preorderLoss || 0,
                    preorderConsume: quantities[product._id]?.preorderConsume || 0,
                    directStockIn: quantities[product._id]?.directStockIn || 0,
                    directPreviousReceipt: quantities[product._id]?.directPreviousReceipt || 0,
                    directNextRetrieval: quantities[product._id]?.directNextRetrieval || 0,
                    directStockOn: quantities[product._id]?.directStockOn || 0,
                    directShrinkage: quantities[product._id]?.directShrinkage || 0,
                    directCombo: quantities[product._id]?.directCombo || 0,
                    directLoss: quantities[product._id]?.directLoss || 0,
                    directConsume: quantities[product._id]?.directConsume || 0,
                })),            
                inventoryUserId: user._id || null,
            };
            
            await api[method](url, payload);
            onInvoiceSaved(selectedFlights);
            onClose();
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to save inventory!");
        }
    };   

    const handleDeleteClick = () => {        
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };
    
    const handleDeleteInvoice = async () => {
        try {
            if (selectedInvoiceId) {
                const url = `/inflightsales/inventory/delete/${selectedInvoiceId}`;
                const payload = { 
                    inventoryUserId: user._id || null,
                };

                await api.put(url, payload);               
                onInvoiceSaved(selectedFlights);
                onClose();
            }
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to delete inventory!");
        }
    };

    const handleCloseDialog = () => {
        setProductList([]);
        setQuantities({});        
        onClose();
    };

    const getTime = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];


    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '95%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween pb={1} borderBottom={`1px solid ${neutralDark}`}>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary"                                                
                    >
                        INVENTORY: {crewCode} - {crewName}
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>                
                <Box p={0.5} >
                    {selectedFlights.length > 0 ? (
                        selectedFlights.map((flight, idx) => (                            
                            <FlexBetween key={idx}>
                                <Typography width="20px" textAlign="center" color="primary">{flight.flightNo}</Typography>
                                <Typography width="44px" textAlign="center">
                                    {timeMode === 'UTC' 
                                        ? format(flight.flightDate, 'ddMMMyy')
                                        : format(flight.flightDateLocalBase, 'ddMMMyy')
                                    }
                                </Typography>
                                <Typography width="25px" textAlign="center">
                                    {getTime(flight, timeMode, 'etd')}
                                </Typography>
                                <Typography width="20px" textAlign="center">{flight.dep}</Typography>
                                <Typography width="20px" textAlign="center">{flight.arr}</Typography>
                                <Typography width="25px" textAlign="center">
                                    {getTime(flight, timeMode, 'eta')}
                                </Typography> 
                                <Typography width="4px" textAlign="center">{flight.pax}</Typography>
                            </FlexBetween>
                        ))
                    ) : (
                        <Typography color="textSecondary">No flight selected for this invoice.</Typography>
                    )}
                </Box> 
                <Box display="flex" flexDirection="column" gap={2}>
                    <FlexBetween pt={0.5} borderTop={`1px solid ${neutralDark}`} >   
                        <Typography>
                            PreorderAmount: {formatNumber(totalPurchaseAmounts.preorderPurchaseAmount)}
                        </Typography>   
                        <Typography>
                            DirectAmount: {formatNumber(totalPurchaseAmounts.directPurchaseAmount)}
                        </Typography>                 
                    </FlexBetween>   

                    <FlexBetween gap={1}>
                        <FormControl sx={{ width: '100%' }}>
                            <InputLabel>Supplier</InputLabel>
                            <Select label="Supplier" size='small' value={selectedSupplier} onChange={handleSupplierChange}>
                                <MenuItem value="none">None</MenuItem>
                                {suppliers.map((supplier) => (
                                    <MenuItem key={supplier._id} value={supplier._id}>{supplier.supplierName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ width: '100%' }}>
                            <InputLabel>Supply Standard</InputLabel>
                            <Select label="Supply Standard" size='small' value={selectedSupplyStandard} onChange={handleSupplyStandardChange}>
                                <MenuItem key="none" value="none">None</MenuItem>
                                {supplyStandards.map((supplyStandard) => (
                                    <MenuItem key={supplyStandard._id} value={supplyStandard._id}>{supplyStandard.supplyStandardName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </FlexBetween>                 
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <FlexCenter sx={{ height: '100%', width: '100%', mt: 1 }}>
                        <CircularProgress />
                    </FlexCenter>
                ) : (
                    productList.map((product, idx) => {
                        const isInvisible = product.isPreorderHidden && product.isDirectHidden;
                        return (
                            !isInvisible && (
                                <Box key={idx} py={1} borderBottom={`1px solid ${neutralDark}`}> 
                                    <FlexBetween gap={2}>
                                        <Typography color="primary" textAlign="left" sx={{ width: isMobile ? '250px' : '370px' }}>
                                            {idx + 1}. {product?.productName}
                                        </Typography>
                                        <Typography width="20px" textAlign="center">{product.unit}</Typography>
                                        <TextField
                                            id={`price-${idx}`}
                                            variant="standard"
                                            type="number"
                                            label="Price"
                                            name="purchasePrice"
                                            onChange={(e) => handlePriceChange(product._id, Math.max(0, parseInt(e.target.value, 10)))}
                                            value={product.purchasePrice || 0}
                                            InputProps={{ inputProps: { min: 0, step: 1000, style: { textAlign: 'center' } } }}
                                            sx={{ width: '80px' }}
                                        />
                                        <Typography color="primary" >
                                            {product?.supplierName}
                                        </Typography>
                                    </FlexBetween>
                                    <Box display="flex" flexDirection={isMobile ? 'row' : 'column'} justifyContent="space-between" gap={2}>
                                        {['Preorder', 'Direct'].map((category) => {
                                            const isHidden = !isMobile && ((category === 'Preorder' && product.isPreorderHidden) || (category === 'Direct' && product.isDirectHidden));
                                            
                                            return (
                                                <Box key={category} display="flex" flexDirection="column" gap={1}>
                                                    {!isHidden && (
                                                        <Box>
                                                            <Typography color="secondary" sx={{ textDecoration: 'underline' }}>
                                                                {category}:
                                                            </Typography>
                                                            <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" gap={1}>
                                                                {[
                                                                    { label: 'Cấp đi', type: 'StockIn' },
                                                                    { label: 'Bàn giao tổ trước', type: 'PreviousReceipt' },
                                                                    { label: 'Thu hồi đổi tổ', type: 'NextRetrieval' },
                                                                    { label: 'Thu hồi thực tế', type: 'StockOn' },
                                                                    { label: 'Hỏng hủy', type: 'Shrinkage' },
                                                                    { label: 'Combo', type: 'Combo' },
                                                                    { label: 'Thất thoát', type: 'Loss' },
                                                                    { label: 'Tiêu thụ', type: 'Consume', disabled: true },
                                                                ].map(({ label, type, disabled }, index) => (
                                                                    <Box key={index} display="flex" flexDirection={isMobile ? 'row' : 'column'} justifyContent="space-between" alignItems="center">
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: 'medium',
                                                                                textAlign: isMobile ? 'left' : 'center'
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </Typography>
                                                                        <QuantityControl
                                                                            productId={product._id}
                                                                            type={`${category.toLowerCase()}${type}`}
                                                                            quantity={quantities[product._id]?.[`${category.toLowerCase()}${type}`] || 0}
                                                                            handleQuantityChange={handleQuantityChange}
                                                                            disabled={disabled}
                                                                            sx={{ minWidth: '100px' }}
                                                                        />
                                                                    </Box>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );                                    
                                        })}
                                    </Box>
                                </Box>
                            )                    
                        )
                    })
                )}                
            </DialogContent>
            <DialogActions>             
                {error && <Typography color="error">{error.message}</Typography>}  
                {selectedInvoiceId && selectedInventoryProducts.length > 0 && (
                    <Button 
                        variant="outlined" 
                        color="error" 
                        disabled={loading || isProcessingTimeOut}
                        onClick={() => {
                            if (!hasRight(permissionMap, "Inventory", "delete")) return;
                            handleDeleteClick();
                        }}
                    >
                        Delete
                    </Button>
                )}              
                <Button 
                    variant="outlined" 
                    disabled={loading || isProcessingTimeOut} 
                    onClick={() => {
                        if (!hasRight(permissionMap, "Inventory", "create")) return;
                        handleSaveInvoice();
                    }}
                >
                    Save
                </Button>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleCloseDialog}
                >
                    Close
                </Button>
            </DialogActions>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleDeleteInvoice}
                deleteType={deleteType}
            />
        </Dialog>
    );
};

export default InventoryDialog;